import { useSession } from '@/lib/auth-client';
import { getActiveTenant, isSubscriptionActive } from '@/lib/tenant';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';

export function AuthGate({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const location = useRouterState({ select: (state) => state.location });
    const { data: session, isPending } = useSession();
    const hasRedirected = useRef(false);
    const isAuthenticated = Boolean(session?.user?.id);
    const isPublicRoute =
        location.pathname === '/' ||
        location.pathname.startsWith('/auth') ||
        location.pathname.startsWith('/blog') ||
        location.pathname === '/faq' ||
        location.pathname === '/terms' ||
        location.pathname === '/security' ||
        location.pathname === '/privacy-policy';
    const isBillingRoute =
        location.pathname === '/account' ||
        location.pathname.startsWith('/subscribe');
    const requiresSubscription = !isPublicRoute && !isBillingRoute;
    const shouldLoadSubscription = isAuthenticated && !isPublicRoute;
    const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(
        null,
    );
    const [isSubscriptionPending, setIsSubscriptionPending] = useState(false);
    const isSubscriptionKnown = subscriptionStatus !== null;

    useEffect(() => {
        if (isPending) {
            return;
        }

        if (!isAuthenticated && !isPublicRoute) {
            if (!hasRedirected.current) {
                hasRedirected.current = true;
                navigate({ to: '/auth', replace: true });
            }
            return;
        }

        hasRedirected.current = false;
    }, [
        isPending,
        isAuthenticated,
        location.pathname,
        navigate,
        isPublicRoute,
    ]);

    useEffect(() => {
        if (isPending || !shouldLoadSubscription) {
            return;
        }

        if (subscriptionStatus && isSubscriptionActive(subscriptionStatus)) {
            return;
        }

        let isMounted = true;
        if (subscriptionStatus === null) {
            queueMicrotask(() => {
                if (!isMounted) {
                    return;
                }
                setIsSubscriptionPending(true);
            });
        }

        void getActiveTenant({ force: true })
            .then((tenant) => {
                if (!isMounted) {
                    return;
                }
                setSubscriptionStatus(tenant.subscriptionStatus);
            })
            .catch(() => {
                if (!isMounted) {
                    return;
                }
                // Fail closed: treat unknown as inactive.
                setSubscriptionStatus('inactive');
            })
            .finally(() => {
                if (!isMounted) {
                    return;
                }
                if (subscriptionStatus === null) {
                    setIsSubscriptionPending(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [isPending, shouldLoadSubscription, subscriptionStatus]);

    useEffect(() => {
        if (
            !requiresSubscription ||
            isSubscriptionPending ||
            !isSubscriptionKnown
        ) {
            return;
        }

        if (!isSubscriptionActive(subscriptionStatus)) {
            navigate({ to: '/subscribe', replace: true });
            return;
        }
    }, [
        requiresSubscription,
        isSubscriptionPending,
        subscriptionStatus,
        navigate,
    ]);

    if (isPublicRoute) {
        return <>{children}</>;
    }

    if (isPending) {
        return (
            <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
                Checking session...
            </div>
        );
    }

    if (
        requiresSubscription &&
        (isSubscriptionPending || !isSubscriptionKnown)
    ) {
        return (
            <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
                Checking subscription...
            </div>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    if (requiresSubscription && !isSubscriptionActive(subscriptionStatus)) {
        return null;
    }

    return <>{children}</>;
}
